import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock } from "lucide-react";
import "./popMenu.scss";

interface PopupModalProps {
  show: boolean;
  onClose: () => void;
  title: string;
  description: string;
  type?: "accessDenied" | "ceo" | "confidential";
  deniedDocument?: string;
  requirements?: string[];
  lockedDocs?: string[];
  showRequirements?: boolean;
}

interface AccessDeniedModalProps {
  showAccessDenied: boolean;
  setShowAccessDenied: (value: boolean) => void;
  deniedDocument: string;
}
const PopupModal: React.FC<PopupModalProps> = ({
  show,
  onClose,
  title,
  description,
  type = "confidential",
  deniedDocument,
  requirements = [],
  lockedDocs = [],
  showRequirements = false,
}) => {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="popup-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className={`popup-container ${type === "ceo" ? "ceo" : ""}`}
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-btn" onClick={onClose}>
              <X className="close-icon" />
            </button>

            <div className="popup-content">
              {type !== "ceo" && (
                <motion.div
                  className="popup-icon-container"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                >
                  <div className="popup-icon-glow" />
                  <div className="popup-icon">
                    <Lock className="lock-icon" strokeWidth={2} />
                  </div>
                </motion.div>
              )}

              <h3 className={`popup-title ${type === "ceo" ? "dark" : ""}`}>
                {title}
              </h3>

              {deniedDocument && (
                <div className="denied-document">
                  <span>{deniedDocument}</span>
                </div>
              )}

              <p
                className={`popup-description ${type === "ceo" ? "dark-text" : ""}`}
              >
                {description}
              </p>

              {showRequirements && (
                <div className="requirement-grid">
                  <div className="requirement available">
                    <div className="requirement-header">
                      <div className="circle green">✓</div>
                      <span>Available</span>
                    </div>
                    <ul>
                      {requirements.map((item) => (
                        <li key={item}>
                          <span className="dot green"></span> {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="requirement locked">
                    <div className="requirement-header">
                      <div className="circle orange">
                        <Lock size={12} />
                      </div>
                      <span>Locked</span>
                    </div>
                    <ul>
                      {lockedDocs.map((item) => (
                        <li key={item}>
                          <span className="dot orange"></span> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <motion.button
                className="popup-button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={()=>{onClose()}}
              >
                Understood
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PopupModal;
export const PandingModal: React.FC<AccessDeniedModalProps> = ({
  showAccessDenied,
  setShowAccessDenied,
  deniedDocument,
}) => {
  return (
    <AnimatePresence>
      {showAccessDenied && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="access-modal-overlay"
          onClick={() => setShowAccessDenied(false)}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="access-modal-container"
          >
            {/* Close Button */}
            <button
              onClick={() => setShowAccessDenied(false)}
              className="access-modal-close"
            >
              <X className="icon-close" />
            </button>

            {/* Content */}
            <div className="access-modal-content">
              {/* Animated Lock */}
              <motion.div
                className="access-modal-lock"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              >
                <motion.div
                  animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="access-modal-ring"
                />
                <motion.div
                  className="access-modal-lock-circle"
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <motion.div
                    animate={{ rotate: [0, -5, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <Lock className="icon-lock" strokeWidth={2} />
                  </motion.div>
                </motion.div>
              </motion.div>

              {/* Title */}
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="access-modal-title"
              >
                Access Restricted
              </motion.h3>

              {/* Document Name */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                className="access-modal-badge"
              >
                <span>{deniedDocument}</span>
              </motion.div>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="access-modal-desc"
              >
                This premium content is currently locked. Complete the prerequisite reading to unlock access.
              </motion.p>

              {/* Required Reading */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="access-modal-requirements"
              >
                <p>Required Reading:</p>
                <div className="req-list">
                  {["Intro", "FAQ", "Fact Sheet"].map((doc, index) => (
                    <motion.span
                      key={doc}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.7 + index * 0.1 }}
                      className="req-item"
                    >
                      {doc}
                    </motion.span>
                  ))}
                </div>
              </motion.div>

              {/* CTA Button */}
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                onClick={() => setShowAccessDenied(false)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="access-modal-btn"
              >
                Got it, I’ll read them first
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const AccessDeniedModal: React.FC<AccessDeniedModalProps> = ({
  showAccessDenied,
  setShowAccessDenied,
  deniedDocument,
}) => {
  return (
    <AnimatePresence>
      {showAccessDenied && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="access-modal-overlay"
          onClick={() => setShowAccessDenied(false)}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="access-modal-container"
          >
            {/* Close Button */}
            <button
              onClick={() => setShowAccessDenied(false)}
              className="access-modal-close"
            >
              <X className="icon-close" />
            </button>

            {/* Content */}
            <div className="access-modal-content">
              {/* Animated Lock */}
              <motion.div
                className="access-modal-lock"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              >
                <motion.div
                  animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="access-modal-ring"
                />
                <motion.div
                  className="access-modal-lock-circle"
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <motion.div
                    animate={{ rotate: [0, -5, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <Lock className="icon-lock" strokeWidth={2} />
                  </motion.div>
                </motion.div>
              </motion.div>

              {/* Title */}
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="access-modal-title"
              >
                Access Deniend
              </motion.h3>

              {/* Document Name */}
              {/* <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                className="access-modal-badge"
              >
                <span>{deniedDocument}</span>
              </motion.div> */}

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="access-modal-desc"
              >
                We greatly appreciate your interest in TICCA, and we hope our paths cross again in the future.
              </motion.p>

              {/* Required Reading */}
              {/* <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="access-modal-requirements"
              >
                <p>Required Reading:</p>
                <div className="req-list">
                  {["Intro", "FAQ", "Fact Sheet"].map((doc, index) => (
                    <motion.span
                      key={doc}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.7 + index * 0.1 }}
                      className="req-item"
                    >
                      {doc}
                    </motion.span>
                  ))}
                </div>
              </motion.div> */}

              {/* CTA Button */}
              {/* <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                onClick={() => setShowAccessDenied(false)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="access-modal-btn"
              >
                Got it, I’ll read them first
              </motion.button> */}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};